import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, subDays, isSameDay, startOfDay, isToday, isTomorrow, addDays, startOfMonth, endOfMonth, eachDayOfInterval, differenceInCalendarDays } from 'date-fns';
import ReactECharts from 'echarts-for-react';

const { FiCalendar, FiTruck } = FiIcons;

const Dashboard = ({ orders, varieties, batches }) => {
  const today = new Date();

  // --- Calculate Tasks for Counts ---
  const productionTasks = orders.flatMap(order => {
    // Sudden Orders
    if (order.isSudden) {
      return order.items.map((item, itemIdx) => {
        const variety = varieties.find(v => v.id === item.varietyId);
        if (!variety) return null;

        const deliveryDate = new Date(order.baseDate);
        const taskId = `${order.id}-${item.varietyId}-${itemIdx}`;
        const batch = batches ? batches.find(b => b.taskId === taskId) : null;

        return {
          taskId,
          variety,
          deliveryDate,
          batch,
          isSudden: true,
          sowingDate: null
        };
      }).filter(Boolean);
    }

    // Standard Orders
    return order.items.map((item, itemIdx) => {
      const variety = varieties.find(v => v.id === item.varietyId);
      if (!variety) return null;

      const base = new Date(order.baseDate);
      const totalCycle = (variety.growingDays || 0) + (variety.darkTreatmentDays || 0);
      let sowingDate, deliveryDate;

      if (order.dateType === 'delivery') {
        deliveryDate = base;
        sowingDate = subDays(base, totalCycle);
      } else {
        sowingDate = base;
        deliveryDate = addDays(base, totalCycle);
      }

      const taskId = `${order.id}-${item.varietyId}-${itemIdx}`;
      const batch = batches ? batches.find(b => b.taskId === taskId) : null;

      return {
        taskId,
        variety,
        sowingDate,
        deliveryDate,
        batch,
        isSudden: false
      };
    }).filter(Boolean);
  });

  const sowingCount = productionTasks.filter(task =>
    !task.isSudden && !task.batch && task.sowingDate && (isToday(task.sowingDate) || task.sowingDate < today)
  ).length;

  const deliveryCount = productionTasks.filter(task => {
    if (task.isSudden) {
      return !task.batch?.delivered && (isToday(task.deliveryDate) || isTomorrow(task.deliveryDate));
    }
    return task.batch && task.batch.actualHarvestDate && !task.batch.delivered &&
      (isToday(task.deliveryDate) || isTomorrow(task.deliveryDate));
  }).length;

  // --- Chart Data Preparation ---
  const [salesViewMode, setSalesViewMode] = React.useState('monthly'); // 'monthly' | 'yearly'

  // Helper: Get variety price
  const getVariety = (id) => varieties.find(v => v.id === id);

  // 1. Monthly Data (Daily for current Month)
  const currentMonthStart = startOfMonth(today);
  const currentMonthEnd = endOfMonth(today);
  const daysInMonth = eachDayOfInterval({ start: currentMonthStart, end: currentMonthEnd });

  const monthlySalesTrend = daysInMonth.map(date => {
    const dailyValue = orders.reduce((total, order) => {
      const orderDate = startOfDay(new Date(order.baseDate));
      if (isSameDay(orderDate, date)) {
        const orderVal = order.items.reduce((sum, item) => {
          const v = getVariety(item.varietyId);
          return sum + (parseFloat(item.quantity || 0) * (v ? parseFloat(v.price || 0) : 0));
        }, 0);
        return total + orderVal;
      }
      return total;
    }, 0);

    return {
      label: format(date, 'MMM dd'),
      value: dailyValue
    };
  });

  // 2. Yearly Data (Monthly for this year)
  // Simple: Jan to Dec of current year
  const monthsOfYear = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(today.getFullYear(), i, 1);
    return d;
  });

  const yearlySalesTrend = monthsOfYear.map(monthDate => {
    const monthValue = orders.reduce((total, order) => {
      const orderDate = new Date(order.baseDate);
      if (orderDate.getFullYear() === monthDate.getFullYear() && orderDate.getMonth() === monthDate.getMonth()) {
        const orderVal = order.items.reduce((sum, item) => {
          const v = getVariety(item.varietyId);
          return sum + (parseFloat(item.quantity || 0) * (v ? parseFloat(v.price || 0) : 0));
        }, 0);
        return total + orderVal;
      }
      return total;
    }, 0);

    return {
      label: format(monthDate, 'MMM'),
      value: monthValue
    };
  });

  const activeSalesData = salesViewMode === 'monthly' ? monthlySalesTrend : yearlySalesTrend;

  // 2. Product Mix Data (Bar Chart: Totals)
  const [productViewMode, setProductViewMode] = React.useState('monthly');
  const [productCategoryGroup, setProductCategoryGroup] = React.useState('All'); // 'All' | 'Microgreens' | 'Lettuce' | ...

  const getFilteredOrdersForProductMix = () => {
    let subset = [];
    if (productViewMode === 'monthly') {
      const start = startOfMonth(today);
      const end = endOfMonth(today);
      subset = orders.filter(o => {
        const d = new Date(o.baseDate);
        return d >= start && d <= end;
      });
    } else {
      const year = today.getFullYear();
      subset = orders.filter(o => new Date(o.baseDate).getFullYear() === year);
    }
    return subset;
  };

  const filteredOrders = getFilteredOrdersForProductMix();

  // Aggregate Data
  // If 'All' -> Show by Category
  // If Specific Group -> Show by Variety within that group
  const mixDataMap = {};

  filteredOrders.forEach(order => {
    order.items.forEach(item => {
      const v = getVariety(item.varietyId);
      if (v) {
        const qty = parseFloat(item.quantity || 0);
        const cat = v.category || 'Microgreens';

        if (productCategoryGroup === 'All') {
          // Aggregate by Category
          if (!mixDataMap[cat]) mixDataMap[cat] = 0;
          mixDataMap[cat] += qty;
        } else {
          // Aggregate by Variety if matches category
          if (cat === productCategoryGroup) {
            if (!mixDataMap[v.name]) mixDataMap[v.name] = 0;
            mixDataMap[v.name] += qty;
          }
        }
      }
    });
  });

  // Color Palette for Charts
  const CHART_COLORS = [
    '#16a34a', '#2563eb', '#db2777', '#ca8a04', '#9333ea',
    '#0891b2', '#ea580c', '#dc2626', '#4f46e5', '#059669'
  ];

  // Transform to Chart Data
  let productMixData = [];
  if (productCategoryGroup === 'All') {
    // Ensure all standard cats exist for consistency or just show what has data? 
    // User likely wants to see specific ones.
    // Let's use the dynamic map.
    productMixData = Object.entries(mixDataMap).map(([name, val], index) => ({
      name,
      value: val,
      itemStyle: {
        color: name === 'Microgreens' ? '#16a34a' :
          name === 'Edible Flowers' ? '#ec4899' :
            name === 'Lettuces' ? '#84cc16' : CHART_COLORS[index % CHART_COLORS.length]
      }
    }));
  } else {
    // Varieties
    productMixData = Object.entries(mixDataMap).map(([name, val], index) => ({
      name,
      value: val,
      itemStyle: { color: CHART_COLORS[index % CHART_COLORS.length] } // Different colors
    }));
  }

  // Sort descending
  productMixData.sort((a, b) => b.value - a.value);

  // Get unique categories for the filter buttons
  const uniqueCategories = ['All', ...[...new Set(varieties.map(v => v.category).filter(Boolean))].sort()];

  // --- ECharts Options ---
  const lineOption = {
    title: { text: salesViewMode === 'monthly' ? 'Monthly Sales Trend' : 'Yearly Sales Trend', left: 'center', textStyle: { fontWeight: 'lighter', color: '#1e3a8a' } },
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: activeSalesData.map(d => d.label), axisLine: { lineStyle: { color: '#ccc' } } },
    yAxis: { type: 'value', axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed' } } },
    series: [{
      name: 'Sales (RM)',
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: 'rgba(22, 163, 74, 0.3)' }, { offset: 1, color: 'rgba(22, 163, 74, 0.0)' }]
        }
      },
      lineStyle: { width: 3, color: '#16a34a' },
      itemStyle: { color: '#16a34a', borderWidth: 2, borderColor: '#fff' },
      data: activeSalesData.map(d => d.value)
    }]
  };

  // Axis Label Logic
  let yAxisLabel = 'Qty';
  let xAxisLabel = 'Category';

  if (productCategoryGroup === 'Edible Flowers') {
    yAxisLabel = 'g';
    xAxisLabel = 'Varieties';
  } else if (productCategoryGroup === 'Microgreens') {
    yAxisLabel = 'Pun';
    xAxisLabel = 'Varieties';
  } else if (productCategoryGroup === 'Lettuces') {
    yAxisLabel = 'Pcs';
    xAxisLabel = 'Varieties';
  } else if (productCategoryGroup !== 'All') {
    xAxisLabel = 'Varieties';
  }

  const barOption = {
    title: { text: `Product Mix (${productCategoryGroup})`, left: 'center', textStyle: { fontWeight: 'lighter', color: '#1e3a8a' }, subtext: productViewMode === 'monthly' ? 'Monthly' : 'Yearly' },
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', name: xAxisLabel, nameLocation: 'middle', nameGap: 30, data: productMixData.map(d => d.name), axisLine: { lineStyle: { color: '#ccc' } }, axisLabel: { interval: 0, rotate: 30 } },
    yAxis: { type: 'value', name: yAxisLabel, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed' } } },
    series: [{
      name: yAxisLabel,
      type: 'bar',
      barWidth: '50%',
      data: productMixData,
      label: { show: true, position: 'top' }
    }]
  };

  const StatCard = ({ title, count, icon, color, description }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 hover:shadow-lg transition-all duration-300">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-widest">{title}</p>
          <p className="text-4xl font-light text-[#1e3a8a] mt-1">{count !== undefined ? count : '-'}</p>
          <p className="text-xs text-gray-400 mt-1 font-light">{description}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center shadow-lg shadow-gray-100 transform rotate-6 hover:rotate-0 transition-transform`}>
          <SafeIcon icon={icon} className="text-xl text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">Dashboard Overview</h1>
          <p className="text-gray-400 mt-2 font-light">Performance metrics & insights</p>
        </div>
        <div className="flex items-center space-x-3 text-gray-400 font-medium bg-white px-4 py-2 rounded-xl border border-gray-100 shadow-sm">
          <SafeIcon icon={FiCalendar} />
          <span className="uppercase tracking-widest text-[11px]">{format(today, 'EEEE, MMMM dd, yyyy')}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <StatCard title="To Sow" count={sowingCount} icon={FiCalendar} color="bg-[#16a34a]" description="Seeds to start" />
        <StatCard title="To Delivery" count={deliveryCount} icon={FiTruck} color="bg-[#1e3a8a]" description="Orders to fulfill" />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Left Column: Charts (2/3 width) */}
        <div className="lg:col-span-2 space-y-8">
          {/* Sales Trend Chart */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm relative">
            <div className="absolute top-6 right-6 z-10 flex space-x-2 bg-gray-50 p-1 rounded-lg">
              <button
                onClick={() => setSalesViewMode('monthly')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${salesViewMode === 'monthly' ? 'bg-white text-[#16a34a] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSalesViewMode('yearly')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${salesViewMode === 'yearly' ? 'bg-white text-[#16a34a] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
              >
                Yearly
              </button>
            </div>
            <ReactECharts option={lineOption} style={{ height: '350px' }} />
          </div>

          {/* Product Mix Chart */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm relative">
            <div className="absolute top-6 right-6 z-10 flex flex-col space-y-2 items-end">
              <select
                value={productCategoryGroup}
                onChange={(e) => setProductCategoryGroup(e.target.value)}
                className="text-xs bg-gray-50 border border-gray-200 rounded-md px-2 py-1 outline-none focus:border-[#16a34a] text-gray-600 mb-1"
              >
                {uniqueCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              <div className="flex space-x-2 bg-gray-50 p-1 rounded-lg">
                <button
                  onClick={() => setProductViewMode('monthly')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${productViewMode === 'monthly' ? 'bg-white text-[#16a34a] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setProductViewMode('yearly')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${productViewMode === 'yearly' ? 'bg-white text-[#16a34a] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  Yearly
                </button>
              </div>
            </div>
            <ReactECharts option={barOption} style={{ height: '350px' }} />
          </div>
        </div>

        {/* Right Column: Buffer Inventory (1/3 width) */}
        <div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full max-h-[800px]">
            <div className="p-6 border-b border-gray-50 bg-gray-50/30">
              <h3 className="text-lg font-light text-[#1e3a8a]">Live Buffer Inventory</h3>
              <p className="text-xs text-gray-400 mt-1">Available stock for sudden orders</p>
            </div>
            <div className="overflow-y-auto p-4 space-y-3 custom-scrollbar flex-1">
              {(() => {
                // Calculate Stock Logic
                const stockMap = varieties.map(v => {
                  const today = new Date();
                  // 1. Total Produced Buffer (Last 7 Days)
                  const produced = orders.reduce((acc, order) => {
                    if (order.isSudden) return acc;
                    const item = order.items.find(i => i.varietyId === v.id);
                    if (!item || !item.bufferQuantity) return acc;

                    const base = new Date(order.baseDate);
                    let harvestDate = base;
                    if (order.dateType === 'sowing') {
                      const days = (v.growingDays || 0) + (v.darkTreatmentDays || 0);
                      harvestDate = addDays(base, days);
                    }

                    const diff = differenceInCalendarDays(today, harvestDate);
                    if (diff >= 0 && diff <= 7) return acc + (parseFloat(item.bufferQuantity) || 0);
                    return acc;
                  }, 0);

                  // 2. Total Consumed by Sudden Orders (Last 7 Days)
                  const consumed = orders.reduce((acc, order) => {
                    if (!order.isSudden) return acc;
                    const item = order.items.find(i => i.varietyId === v.id);
                    if (!item) return acc;

                    const orderDate = new Date(order.baseDate);
                    const diff = differenceInCalendarDays(today, orderDate);
                    if (diff >= 0 && diff <= 7) return acc + (parseFloat(item.quantity) || 0);
                    return acc;
                  }, 0);

                  return {
                    ...v,
                    stock: Math.max(0, produced - consumed)
                  };
                })
                  .filter(v => {
                    const cat = (v.category || 'Microgreens').toLowerCase();
                    return v.stock > 0 && (cat === 'microgreens' || cat === 'lettuce' || cat === 'lettuces');
                  })
                  .sort((a, b) => (a.name || '').trim().toLowerCase().localeCompare((b.name || '').trim().toLowerCase()));

                if (stockMap.length === 0) {
                  return (
                    <div className="text-center py-10">
                      <SafeIcon icon={FiIcons.FiPackage} className="text-gray-200 text-4xl mx-auto mb-2" />
                      <p className="text-gray-400 text-sm font-light">No buffer stock available.</p>
                    </div>
                  );
                }

                return stockMap.map(v => (
                  <div key={v.id} className="flex items-center p-3 rounded-xl bg-white border border-gray-100 hover:border-blue-100 transition-colors shadow-sm">
                    <div className="w-10 h-10 rounded-lg bg-gray-50 flex-shrink-0 border border-gray-50 flex items-center justify-center overflow-hidden mr-3">
                      {v.image ? (
                        <img src={v.image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={FiIcons.FiImage} className="text-gray-300 text-xs" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{v.name}</p>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider">{v.category || 'Microgreens'}</p>
                    </div>
                    <div className="flex flex-col items-end pl-2">
                      <span className="text-lg font-bold text-[#16a34a] leading-none">{v.stock}</span>
                      <span className="text-[9px] text-gray-400 uppercase">Avail</span>
                    </div>
                  </div>
                ));
              })()}
            </div>
            <div className="p-4 bg-gray-50/50 text-center border-t border-gray-50">
              <p className="text-[10px] text-gray-400">Inventory updates automatically with orders</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;